<script lang="ts">
	//Svelte hooks
	import { afterUpdate, beforeUpdate } from 'svelte';

	//Stores
	import { home_data } from 'src/stores/account/user';
	import { updating_devices } from 'src/stores/device-runtime/process';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { rooms } from 'src/stores/lists/rooms';
	import { edit, update, bulkActionEdit } from 'src/stores/ui/edit_lists';
	import { for_update } from 'src/stores/device-runtime/firmware-ui';
	import { defaultStateType, getDeviceState } from 'src/stores/device-runtime/states';

	//Helpers
	import { status_configurator } from 'src/helpers/device_data_helpers';
	import {
		load_image_from,
		get_gen_2_component_channel_structure,
		is_gen_2,
		real_id,
		get_gen_2_control_component_channel_structure,
	} from 'src/helpers/interface_helpers';
	import { devices_config } from 'src/configs/devices';
	import { getGen1DeviceStatuses } from 'src/components/Sidepanel/SidePanelLayoutProvider/SidePanelDeviceLayout/Devices/Shelly/Gen1/gen1-main-helpers';

	//Design system components
	import {
		AdvancedCard,
		AdvancedCardV2,
		Label,
		LabelList,
		LabelListV2,
		LabelV2,
	} from '@Shelly/shelly-design-system';

	//Components
	import StatusBar from './StatusBar.svelte';
	import MainControlStatusSlot from './MainControlStatusSlot.svelte';
	import TimerRemaining from 'src/components/TimerRemaining/TimerRemaining.svelte';
	import { getCustomizeLabelList } from 'src/components/DevicesSettings/CustomizeDeviceWizard/customize-device-wizard-helpers';
	import { format } from 'svelte-i18n';
	import type { AiModelData } from 'src/types/AIDeviceModelType';
	import {
		getCriticalNotifications,
		getNotificationsForDevice,
	} from 'src/helpers/notifications/device_notifications_helper';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	import { sidePanelStateCombined } from 'src/stores/ui/sidepanel-public';
	import { getAddonSupported } from 'src/helpers/addons/addon_support_helpers';
	import { getAddonStatus } from 'src/helpers/addons/addon_helpers';
	import {
		mapIconSolidWeightToLight,
		mapLegacyColorToToken,
		mapOldCustomIconFromDBtoNew,
		type DeviceListLayoutType,
	} from 'src/helpers/new-layout-helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import DashDeviceQuickControl from 'src/components/Dashboards/DashWidgets/Components/DashDeviceWidgetCard/DashDeviceQuickControl.svelte';
	import { checkDeviceForceUpdate } from 'src/helpers/firmware_helpers';

	export let device;
	export let aiModelState: AiModelData = {
		reset_ai_ts: 0,
		ai_model_readiness: 0,
		ai_start_learning_ts: 0,
		ai_remaining_hours: 0,
	};
	export let type: string = '';
	export let variant: string = '';
	export let disableSelected = false;
	export let hideSlotRight = false;
	export let disableSelection = false;

	let title = device.name,
		description = {},
		subtitle = {},
		avatar = {
			image: device?.image
				? load_image_from(device?.image, device?.localImage ? '' : device?.server).replace(
						'thumb_',
						'',
					)
				: undefined,
			backgroundColor:
				!!device.backgroundColor && device.backgroundColor !== ''
					? device.backgroundColor
					: 'color-base',
			icon: mapOldCustomIconFromDBtoNew(device.icon),
			notification: null,
		};

	let isLoading = false;
	let emeter_timer;
	let gen_1_timer;
	let gen_2_emeter_timer;
	let gen_2_switch_addon_emeter_timer;
	let gen_2_timer;
	let hideSlotMainStatusParam = false;
	let deviceState = getDeviceState(device.id) as any;
	let timerLabels: Record<string, any> = {};
	let deviceListType: DeviceListLayoutType = 'default';

	$: combinedLabelList = [...Object.values(timerLabels), ...labelList];

	$: statusStore = getDeviceStatus(device.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device.id);
	$: settings = $settingsStore;
	$: isCondensed = variant === 'condensed';
	$: isLoading = $deviceState?.isLoading;

	$: deviceConfig = devices_config?.[device?.type];
	$: isGen2 = is_gen_2(device);
	$: statuses = isGen2
		? status_configurator(status, device, settings)
		: getGen1DeviceStatuses(status, device, settings, deviceConfig);
	$: labelList = getCustomizeLabelList(device, statuses);

	$: notificationsToShow = getNotificationsForDevice(device?.id);
	$: criticalNotifications = getCriticalNotifications(notificationsToShow);
	$: hideLabelsForMandatoryUpdate = settings && checkDeviceForceUpdate(device) && status?.online;
	$: visibleCriticalNotifications = hideLabelsForMandatoryUpdate ? [] : criticalNotifications;

	$: deviceListType = $home_data?.['device_list']?.type || 'default';

	$: if (visibleCriticalNotifications.length) {
		avatar.notification = {
			type: 'danger',
		};
	} else if (!visibleCriticalNotifications.length) {
		avatar.notification = null;
	}

	beforeUpdate(() => {
		if (Object.keys(device).length) {
			// ToDo: this might be better off abstracted in its own components in stead of listed here
			if ($update.value && $update?.type === 'update') {
				subtitle = {
					value: $for_update[real_id(device.id)]?.fwVersion,
					textSmall: true,
					bold: false,
					type: 'success',
				};
				description = {
					value: $for_update[real_id(device.id)]?.newFwVersion,
				};
				if ($updating_devices[device.id]) {
					description =
						$deviceState?.type !== defaultStateType
							? {
									...$deviceState?.description,
									textSmall: false,
									bold: true,
								}
							: $deviceState?.description;
					subtitle = $deviceState?.subtitle;
				}
			} else if (type === 'group') {
				if (!device.deleted) {
					description =
						$deviceState?.type !== defaultStateType
							? {
									...$deviceState?.description,
									textSmall: false,
									bold: true,
								}
							: $deviceState?.description;
					subtitle = {
						value: $rooms[device?.room_id]?.name,
						textSmall: true,
						bold: false,
					};
				} else {
					subtitle = {
						value: $rooms[device?.room_id]?.name,
						textSmall: true,
						bold: false,
					};
				}
			} else if (!$edit) {
				description =
					$deviceState?.type !== defaultStateType
						? {
								...$deviceState?.description,
								textSmall: false,
								bold: true,
							}
						: $deviceState?.description;
				subtitle = $deviceState?.subtitle;
			}

			if (device.category !== 'sensor') {
				emeter_timer =
					!is_gen_2(device) &&
					device.category === 'emeter' &&
					status?.['relays']?.[0]?.has_timer;
				gen_1_timer =
					!is_gen_2(device) &&
					device.category !== 'emeter' &&
					status?.[device.category + 's']?.[device.channel]?.has_timer;
				gen_2_emeter_timer =
					is_gen_2(device) &&
					typeof status?.[`${get_gen_2_control_component_channel_structure(device)}`]
						?.timer_started_at !== 'undefined';
				gen_2_switch_addon_emeter_timer =
					is_gen_2(device) &&
					device?.category === 'emeter' &&
					getAddonSupported(device, 'prooutput') &&
					typeof status?.['switch:100']?.timer_started_at !== 'undefined';
				gen_2_timer =
					is_gen_2(device) &&
					typeof status?.[`${get_gen_2_component_channel_structure(device)}`]
						?.timer_started_at !== 'undefined';
			}
		}
	});

	afterUpdate(() => {
		if (
			typeof aiModelState !== 'undefined' &&
			Object.values(aiModelState).length !== 0 &&
			$update.value &&
			$update.type === 'ai_device_failure' &&
			($update.subType === 'disable_ai_model' || $update.subType === 'manage_ai_notifications')
		) {
			subtitle = {
				textSmall: true,
				value: $format('ai_model_readiness', {
					values: {
						readiness: aiModelState.ai_model_readiness,
					},
					default: `AI Model progress: ${aiModelState.ai_model_readiness} %`,
				}),
				bold: false,
				icon: 'fak fa-solid-sparkles icon-ai-color',
			};
		}
	});

	const cardClick = () => {
		if ($edit || disableSelected) return;
		setSidePanelState({ type: 'device', id: device.id, open: true });
	};
</script>

{#if typeof $deviceState !== 'undefined' || device?.isDndShadowItem}
	{#if $showNewLayout}
		{#if deviceListType === 'compact'}
			<DashDeviceQuickControl
				editMode={$edit}
				deviceWidget={{ id: device?.id }}
				{disableSelection}
				on:arrow-click={cardClick}
				on:click={cardClick}
				on:handle-drag-start
				on:handle-drag-end
				on:device-select
				on:device-update
			/>
		{:else}
			<AdvancedCardV2
				{isCondensed}
				{avatar}
				{title}
				{subtitle}
				description={$update.value && $update.type === 'account_transfer_devices'
					? device.shared || device.shared_by_me
						? {
								bold: true,
								type: 'disabled',
								value: $format('device_shared_description', {
									default: 'The device is shared and cannot be transferred',
								}),
							}
						: {}
					: $update.value &&
						  $update.type === 'bulk_delete' &&
						  device.channels_count > 1 &&
						  device.channel < 100 &&
						  !device.shared
						? {
								bold: true,
								type: 'disabled',
								value: $format('channel_delete_warning', {
									default: 'Deleting this channel will delete the device',
								}),
							}
						: $deviceState?.type === 3
							? { ...description, medium: true }
							: description}
				optional={$deviceState?.optional}
				hideSlotStatusBar={type === 'group'}
				hideSlotMain={$deviceState?.hideSlotMain || hideSlotMainStatusParam}
				hideSlotRight={$deviceState?.hideSlotRight ||
					hideSlotRight ||
					(device?.mainActionType === 'none' &&
						!$edit &&
						$deviceState?.type == defaultStateType)}
				editMode={$edit}
				selected={device.id === $sidePanelStateCombined?.id}
				isFocusable={!disableSelected}
				hideSlotRightBadge={!visibleCriticalNotifications.length}
				hideSlotEditModeAction={!$edit || !$bulkActionEdit}
				on:handle-drag-start
				on:handle-drag-end
				on:click={cardClick}
			>
				<svelte:fragment slot="statusBar">
					{#if type !== 'group'}
						<StatusBar {device} {status} {settings} />
					{/if}
				</svelte:fragment>

				<svelte:fragment slot="editModeAction">
					{#key $deviceState?.type}
						<MainControlStatusSlot
							{isCondensed}
							{type}
							{device}
							{status}
							{settings}
							{statuses}
							{isLoading}
							notAuthenticated={$deviceState?.type === 9}
							useV2Components={true}
							devBtnsV2Props={{
								deviceButtonV2: { size: 'm' },
								doubleDeviceButtonV2: { size: 'm' },
								tripleDeviceButtonV2: { size: 'm' },
							}}
							{disableSelection}
							on:handle-drag-start
							on:handle-drag-end
							on:edit
							on:delete
							on:device-update
							on:device-select
						/>
					{/key}
				</svelte:fragment>

				<svelte:fragment slot="rightBadge">
					<LabelV2
						size="xs"
						noRadiusType="first"
						value={visibleCriticalNotifications[0].name}
						icon="fas fa-shield-exclamation"
						backgroundColor={visibleCriticalNotifications[0].type === 'danger'
							? '--color-danger'
							: '--color-warning'}
					/>
				</svelte:fragment>

				<svelte:fragment slot="main">
					{#if gen_1_timer}
						{#key status[device.category + 's'][device.channel].timer_started}
							<TimerRemaining
								timerKey="gen1_main"
								{device}
								{status}
								{settings}
								bind:timerLabels
							/>
						{/key}
					{/if}
					{#if emeter_timer}
						{#key status['relays'][0].timer_started}
							<TimerRemaining
								timerKey="gen1_emeter"
								{device}
								{status}
								{settings}
								bind:timerLabels
							/>
						{/key}
					{/if}
					{@const peripheral = getAddonStatus(device, [], false, true)}
					{#if gen_2_switch_addon_emeter_timer && peripheral.find((i) => i.peripheralType == 'switch')}
						{#key status['switch:100'].timer_started_at}
							<TimerRemaining
								timerKey="switch:100"
								{device}
								{status}
								{settings}
								bind:timerLabels
							/>
						{/key}
					{/if}
					{#if gen_2_timer || gen_2_emeter_timer}
						{#key status[get_gen_2_control_component_channel_structure(device)].timer_started_at}
							<TimerRemaining
								timerKey="gen2_main"
								{device}
								{status}
								{settings}
								bind:timerLabels
							/>
						{/key}
					{/if}

					<!-- Render ONCE with all timers stacked first, then original labels -->
					<LabelListV2
						a11yProps={{ isInteractive: true }}
						labelList={combinedLabelList.map((el) => ({
							...el,
							icon: mapIconSolidWeightToLight(el?.icon),
							backgroundColor: mapLegacyColorToToken(el?.type),
						}))}
						size={isCondensed ? 's' : type == 'group' ? 'xs' : 'm'}
						on:label-click={cardClick}
					/>
				</svelte:fragment>

				<svelte:fragment slot="right">
					{#key $deviceState?.type}
						<MainControlStatusSlot
							{isCondensed}
							{type}
							{device}
							{status}
							{settings}
							{statuses}
							{isLoading}
							useV2Components={true}
							devBtnsV2Props={{
								deviceButtonV2: { size: 'm' },
								doubleDeviceButtonV2: { size: 'm' },
								tripleDeviceButtonV2: { size: 'm' },
							}}
							notAuthenticated={$deviceState?.type === 9}
							{disableSelection}
							on:edit
							on:delete
							on:device-update
							on:device-select
						/>
					{/key}
				</svelte:fragment>
			</AdvancedCardV2>
		{/if}
	{:else}
		<AdvancedCard
			{isCondensed}
			{avatar}
			{title}
			{subtitle}
			description={$update.value && $update.type === 'account_transfer_devices'
				? device.shared || device.shared_by_me
					? {
							bold: true,
							type: 'disabled',
							value: $format('device_shared_description', {
								default: 'The device is shared and cannot be transferred',
							}),
						}
					: {}
				: $update.value &&
					  $update.type === 'bulk_delete' &&
					  device.channels_count > 1 &&
					  device.channel < 100 &&
					  !device.shared
					? {
							bold: true,
							type: 'disabled',
							value: $format('channel_delete_warning', {
								default: 'Deleting this channel will delete the device',
							}),
						}
					: description}
			optional={$deviceState?.optional}
			hideSlotStatusBar={type === 'group'}
			hideSlotMain={$deviceState?.hideSlotMain || hideSlotMainStatusParam}
			hideSlotRight={$deviceState?.hideSlotRight ||
				hideSlotRight ||
				(device?.mainActionType === 'none' && !$edit && $deviceState?.type == defaultStateType)}
			editMode={$edit}
			selected={device.id === $sidePanelStateCombined?.id}
			isFocusable={!disableSelected}
			hideSlotRightBadge={!visibleCriticalNotifications.length}
			on:click={cardClick}
		>
			<svelte:fragment slot="statusBar">
				{#if type !== 'group'}
					<StatusBar {device} {status} {settings} />
				{/if}
			</svelte:fragment>

			<svelte:fragment slot="rightBadge">
				<Label
					size="xs"
					noRadiusType="first"
					value={visibleCriticalNotifications[0].name}
					icon="fa-solid fa-shield-exclamation"
					type={visibleCriticalNotifications[0].type === 'danger' ? 'danger' : 'warning'}
				/>
			</svelte:fragment>

			<svelte:fragment slot="main">
				{#if gen_1_timer}
					{#key status[device.category + 's'][device.channel].timer_started}
						<TimerRemaining
							timerKey="gen1_main"
							{device}
							{status}
							{settings}
							bind:timerLabels
						/>
					{/key}
				{/if}
				{#if emeter_timer}
					{#key status['relays'][0].timer_started}
						<TimerRemaining
							timerKey="gen1_emeter"
							{device}
							{status}
							{settings}
							bind:timerLabels
						/>
					{/key}
				{/if}
				{@const peripheral = getAddonStatus(device, [], false, true)}
				{#if gen_2_switch_addon_emeter_timer && peripheral.find((i) => i.peripheralType == 'switch')}
					{#key status['switch:100'].timer_started_at}
						<TimerRemaining
							timerKey="switch:100"
							{device}
							{status}
							{settings}
							bind:timerLabels
						/>
					{/key}
				{/if}
				{#if gen_2_timer || gen_2_emeter_timer}
					{#key status[get_gen_2_control_component_channel_structure(device)].timer_started_at}
						<TimerRemaining
							timerKey="gen2_main"
							{device}
							{status}
							{settings}
							bind:timerLabels
						/>
					{/key}
				{/if}

				<!-- Render ONCE with all timers stacked first, then original labels -->
				<LabelList
					labelList={combinedLabelList}
					type="default"
					size={isCondensed ? 's' : type == 'group' ? 'xs' : 'm'}
					on:label-click={cardClick}
				/>
			</svelte:fragment>

			<svelte:fragment slot="right">
				{#key $deviceState?.type}
					<MainControlStatusSlot
						{isCondensed}
						{type}
						{device}
						{status}
						{settings}
						{statuses}
						{isLoading}
						notAuthenticated={$deviceState?.type === 9}
						{disableSelection}
						on:handle-drag-start
						on:handle-drag-end
						on:edit
						on:delete
						on:device-update
						on:device-select
					/>
				{/key}
			</svelte:fragment>
		</AdvancedCard>
	{/if}
{/if}
